import { Box, Button, CircularProgress, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/utils/api';
import { formatCurrency } from '@/utils/helper';

import BidwarOptionCard from './BidwarOptionCard';

/** @param {import("prop-types").InferProps<typeof StepBidwarsPropTypes>} props */
export default function StepBidwars({ formData, setFormData, nextStep, prevStep }) {
  const { t, i18n } = useTranslation();
  const [bidwars, setBidwars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidwarChoices, setBidwarChoices] = useState(formData.bidwarChoices || {});

  useEffect(() => {
    const fetchBidwars = async () => {
      try {
        const response = await api.get('/api/v2/bidwars/list/');
        setBidwars(response.data);
      } catch (error) {
        // eslint-disable-next-line no-console -- TODO: Use snackbar
        console.error('Error fetching bidwars:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBidwars();
  }, []);

  const handleChoiceChange = (bidwarId, optionId, isOther = false, customValue = '') => {
    setBidwarChoices({
      [bidwarId]: {
        optionId,
        isOther,
        customValue,
      },
    });
  };

  const handleCustomInputChange = (bidwarId, value) => {
    setBidwarChoices(prev => {
      const prevChoice = prev[bidwarId] || {};

      return {
        [bidwarId]: {
          ...prevChoice,
          customValue: value,
        },
      };
    });
  };

  const handleNext = () => {
    setFormData(prev => ({
      ...prev,
      bidwarChoices,
    }));
    nextStep();
  };

  if (loading) return <CircularProgress />;

  if (bidwars.length === 0) {
    return (
      <Box>
        <Typography>{t('donation.nobidwars')}</Typography>
        <Box mt={2}>
          <Button variant="contained" onClick={nextStep}>
            {t('common.continue')}
          </Button>
        </Box>
      </Box>
    );
  }

  const donationAmount =
    typeof formData.amount === 'number'
      ? formData.amount
      : Number.parseFloat(formData.amount || '0');

  // A donation backs a single bidwar, so picking an option elsewhere replaces the previous pick.
  const backedBidwarId = Object.keys(bidwarChoices).find(
    id => bidwarChoices[id]?.optionId || bidwarChoices[id]?.isOther,
  );

  return (
    <Box>
      <Typography gutterBottom variant="h5">
        {t('donation.bidwars.title')}
      </Typography>
      <Typography gutterBottom variant="body1">
        {t('donation.bidwars.info')}
      </Typography>

      <Box role="radiogroup" aria-label={t('donation.bidwars.title')}>
        {bidwars.map(bidwar => {
          const choices = bidwarChoices[bidwar.id] || {};
          const selectedId = choices.optionId;
          const selectedIsOther = choices.isOther;
          const isBacked = String(backedBidwarId) === String(bidwar.id);
          const description = i18n.language === 'fr' ? bidwar.descriptionFr : bidwar.descriptionEn;

          const totalCurrent = bidwar.options.reduce((sum, opt) => sum + opt.total, 0);
          // Only the backed bidwar grows by this donation, so only its bars rescale.
          const totalProjected = totalCurrent + (isBacked ? donationAmount : 0);
          const leadingTotal = Math.max(0, ...bidwar.options.map(opt => opt.total));

          const percentOf = amount => (totalProjected > 0 ? (amount / totalProjected) * 100 : 0);

          // Where this donation would leave the option it backs, against its closest rival.
          const selectedTotal = selectedIsOther
            ? 0
            : (bidwar.options.find(opt => opt.id === selectedId)?.total ?? 0);
          const bestRivalTotal = Math.max(
            0,
            ...bidwar.options.filter(opt => opt.id !== selectedId).map(opt => opt.total),
          );
          const takesLead = selectedTotal + donationAmount > bestRivalTotal;
          const behindBy = bestRivalTotal - (selectedTotal + donationAmount);

          return (
            <Box
              key={bidwar.id}
              sx={[
                {
                  position: 'relative',
                  overflow: 'hidden',
                  marginTop: 3,
                  marginBottom: 3,
                  padding: 2,
                  borderRadius: '2px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderTop: '2px solid',
                  borderTopColor: 'rgba(255,255,255,0.12)',
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
                  transition: 'opacity 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
                  // Same CRT scanline overlay as the milestone tracker.
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.03,
                    backgroundImage:
                      'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(255,255,255,1) 2px, rgba(255,255,255,1) 3px)',
                    pointerEvents: 'none',
                  },
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                },
                isBacked && {
                  borderTopColor: 'warning.main',
                  boxShadow: theme =>
                    `0 -4px 20px -4px ${theme.palette.warning.main}30, inset 0 1px 0 rgba(255,255,255,0.05)`,
                },
                // Dim the bidwars this donation is not backing; hovering one offers the switch.
                backedBidwarId !== undefined
                  && !isBacked && { opacity: 0.6, '&:hover': { opacity: 1 } },
              ]}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 1,
                  marginBottom: 0.5,
                }}
              >
                <Typography
                  className="font-bebas"
                  sx={{
                    fontSize: '0.75rem',
                    letterSpacing: '0.15em',
                    color: isBacked ? 'warning.main' : 'rgba(255,255,255,0.45)',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('donation.bidwars.label')}
                </Typography>
                <Typography
                  className="font-bebas"
                  sx={{
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    color: 'rgba(255,255,255,0.6)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCurrency(totalCurrent, i18n.language)}
                </Typography>
              </Box>

              <Typography
                className="font-oswald"
                sx={{
                  position: 'relative',
                  fontSize: '1.05rem',
                  fontWeight: 500,
                  lineHeight: 1.2,
                  color: 'common.white',
                  textTransform: 'uppercase',
                }}
              >
                {i18n.language === 'fr' ? bidwar.labelFr : bidwar.labelEn}
              </Typography>

              {bidwar.runDetails && (
                <Typography
                  variant="body2"
                  sx={{ position: 'relative', marginTop: 0.5, color: 'rgba(255,255,255,0.45)' }}
                >
                  🎮 {bidwar.runDetails.gameName} — {bidwar.runDetails.category}
                </Typography>
              )}

              {description && (
                <Typography
                  variant="body2"
                  sx={{
                    position: 'relative',
                    marginTop: 1,
                    fontStyle: 'italic',
                    color: 'rgba(255,255,255,0.6)',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {description}
                </Typography>
              )}

              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  marginTop: 2,
                }}
              >
                {bidwar.options.map(option => {
                  const isSelected = option.id === selectedId && !selectedIsOther;

                  return (
                    <BidwarOptionCard
                      key={option.id}
                      bidwarId={bidwar.id}
                      option={option}
                      isSelected={isSelected}
                      isLeading={leadingTotal > 0 && option.total === leadingTotal}
                      currentPercent={percentOf(option.total)}
                      projectedPercent={isSelected ? percentOf(donationAmount) : 0}
                      currentChoice={choices}
                      onSelect={handleChoiceChange}
                      onCustomChange={handleCustomInputChange}
                    />
                  );
                })}

                {bidwar.allowOther && (
                  <BidwarOptionCard
                    isOther
                    bidwarId={bidwar.id}
                    option={{ total: 0 }}
                    isSelected={!!selectedIsOther}
                    currentPercent={0}
                    projectedPercent={selectedIsOther ? percentOf(donationAmount) : 0}
                    currentChoice={choices}
                    onSelect={handleChoiceChange}
                    onCustomChange={handleCustomInputChange}
                  />
                )}
              </Box>

              {isBacked && donationAmount > 0 && (
                <Typography
                  className="font-bebas"
                  sx={[
                    {
                      position: 'relative',
                      marginTop: 1.5,
                      fontSize: '0.75rem',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.6)',
                    },
                    takesLead && {
                      color: 'success.light',
                      '@keyframes bidwarPulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                      },
                      animation: 'bidwarPulse 2s ease-in-out infinite',
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    },
                  ]}
                >
                  {takesLead
                    ? t('donation.bidwars.takesLead')
                    : t('donation.bidwars.behindLead', {
                        amount: formatCurrency(behindBy, i18n.language),
                      })}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box mt={4} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={prevStep}>
          {t('common.back')}
        </Button>
        <Button variant="contained" onClick={handleNext}>
          {t('common.continue')}
        </Button>
      </Box>
    </Box>
  );
}

const StepBidwarsPropTypes = {
  formData: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bidwarChoices: PropTypes.object,
  }).isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
};
StepBidwars.propTypes = StepBidwarsPropTypes;
